import type { ComponentChildren } from "preact";
import { useState } from "react";
import { activeLeaf, NAV } from "../lib/routes";
import { Link, useLoc } from "../lib/nav";
import { ScrollRow } from "./ScrollRow";

type Term = [term: string, meaning: string];

/**
 * Column headers already carry their meaning as a tooltip, which phones never show.
 * Reading them back keeps one source of truth, and lists only the columns on screen.
 */
function readTerms(): Term[] {
	const terms = new Map<string, string>();
	for (const el of document.querySelectorAll<HTMLElement>('#main-scroll [role="columnheader"][title], #main-scroll th[title]')) {
		const term = (el.textContent ?? "").replace(/[↑↓]/g, "").trim();
		if (term && el.title && !terms.has(term)) terms.set(term, el.title);
	}
	return [...terms];
}

function Glossary({ terms }: { terms: Term[] }) {
	if (!terms.length) {
		return <p className="border border-line bg-panel px-2 py-1.5 text-body text-dim">No column on screen needs explaining.</p>;
	}
	return (
		<dl id="view-glossary" className="grid gap-x-6 gap-y-1 border border-line bg-panel px-2 py-1.5 text-body md:grid-cols-2">
			{terms.map(([term, meaning]) => (
				<div key={term} className="flex min-w-0 gap-2">
					<dt className="w-24 shrink-0 font-bold text-fg uppercase">{term}</dt>
					<dd className="min-w-0 text-dim">{meaning}</dd>
				</div>
			))}
		</dl>
	);
}

/** Title row, sibling tabs, one line of context and the view's filters. */
export function ViewHeader({
	title,
	meta,
	desc,
	tabs = true,
	children,
}: {
	title: string;
	meta?: string;
	desc: ComponentChildren;
	tabs?: boolean;
	children?: ComponentChildren;
}) {
	const { path } = useLoc();
	const group = NAV.find((n) => n.children?.some((c) => c.href === path));
	const current = group ? activeLeaf(path, group) : null;
	const [terms, setTerms] = useState<Term[] | null>(null);
	return (
		<header className="shrink-0 space-y-1.5 pb-1">
			<div className="flex items-start justify-between gap-3">
				<div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5">
					<h1 className="text-head font-bold text-bright">
						<span aria-hidden="true" className="text-up">$ </span>
						{title}
					</h1>
					{meta ? <span className="num text-body text-dim">{meta}</span> : null}
				</div>
				<button
					type="button"
					aria-label="Explain the columns"
					aria-expanded={terms !== null}
					title="Explain the columns"
					onClick={() => setTerms(terms ? null : readTerms())}
					className={`ctl shrink-0 max-sm:w-10 max-sm:px-0 ${terms ? "is-on" : ""}`}
				>
					?
				</button>
			</div>
			{tabs && group?.children ? (
				<ScrollRow label={group.label} active={current}>
					{group.children.map((c) => (
						<Link
							key={c.href}
							href={c.href}
							aria-current={c.href === current ? "page" : undefined}
							className={`tab-t shrink-0 ${c.href === current ? "is-on" : ""}`}
						>
							{c.label}
						</Link>
					))}
				</ScrollRow>
			) : null}
			<div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5">
				<p className="min-w-0 flex-1 basis-80 text-data text-dim">{desc}</p>
				{children ? <div className="flex w-full flex-wrap items-center gap-1.5 sm:w-auto">{children}</div> : null}
			</div>
			{terms ? <Glossary terms={terms} /> : null}
		</header>
	);
}
